import {isObject} from 'lodash';

const logger = console;

export class ElasticSearchWhereClause {


  /**
   * add a where clause entry to an array. This will be called when the where clause is an array rathear than an object
   * @param key - the key
   * @param value - the value
   * @param whereClause - where clause
   */
  public static addWhereClauseEntryToArray(key, value, whereClause, additionalWhereClauseArray) {

    if (Array.isArray(value)) {

      for (let i = 0; i < value.length; i++) {

        const whereClauseEntry = {
          key: key,
          value: value[i]
        };

        if (!additionalWhereClauseArray || i === 0) {
          if (Array.isArray(whereClause)) {
            whereClause.push(whereClauseEntry);
          } else {
            whereClause[key] = value[i];
          }
        } else if (additionalWhereClauseArray) {
          additionalWhereClauseArray.push(whereClauseEntry);
        }
      }
    } else {

      const whereClauseEntry = {
        key: key,
        value: value
      };

      if (Array.isArray(whereClause)) {
        whereClause.push(whereClauseEntry);
      } else {
        whereClause[key] = value;
      }
    }
  }

  /**
   * add an entry to the where clause. whereClause will be passed as an object and the key and value will be added as a
   * key/value pair
   * @param whereClause - the initial whereClause. This can only have one occurrence of each field
   * @param key - key
   * @param value - value
   */
  private static addWhereClauseEntry(whereClause, key, value, additionalWhereClauseArray) {
    // we can only have one entry per field because the initial query run for Dexie can only deal with one occurrence of a
    // field in the initial query - any additional criteria on the same field need to be filtered out after running the
    // initial query. Eg. You can't query on appTags = "ticket" and appTags = "water" in the same initial where query.
    // For this reason, we need to add only one occurrence of each field to the initial where clause and then add any
    // additional occurrences to a where clause that can be used in filtering the documents after the initial query has
    // run
    const keyContainsDot = key.indexOf('.') > 0;

    // if the where clause is an array, there is no need to add entries to the additionalWhereClause
    if (Array.isArray(whereClause)) {

      this.addWhereClauseEntryToArray(key, value, whereClause, null);

    } else {

      if (keyContainsDot || whereClause[key]) {

        this.addWhereClauseEntryToArray(key, value, additionalWhereClauseArray, null);

      } else {

        this.addWhereClauseEntryToArray(key, value, whereClause, additionalWhereClauseArray);

      }
    }
  }

  /**
   * add each entry in the ElasticSearch query to a must query that can be passed to the dexie where function as an object.
   * Terms queries are split to some arrays of term and only handled in must_not to not match all the values.
   * @param whereClause - where clause
   * @param elasticSearchClause - elastic search clause
   * @param additionalWhereClauseArray - additional where clause array
   * @param shouldHandleTerms - should handle term
   * @returns boolean - the result
   */
  public static addMustQueryToWhereClause(whereClause, elasticSearchClause, additionalWhereClauseArray, shouldHandleTerms) {

    if (!isObject(whereClause)) {
      logger.error('Please pass the where clause as an object to addMustQueryToWhereClause');
      return false;
    }

    let hasAdded = false;
    if (elasticSearchClause) {

      if (Array.isArray(elasticSearchClause)) {
        for (let i = 0; i < elasticSearchClause.length; i++) {

          // match - {key: {query: value}}
          // match - {key: value}
          if (elasticSearchClause[i]['match']) {
            elasticSearchClause[i]['match'].forEach(function(bean, key) {
              const value = bean.query ? bean.query : bean;
              this.addWhereClauseEntry(whereClause, key, value, additionalWhereClauseArray);
              hasAdded = true;
            });
          }

          // match_phrase - {key: {query: value}}
          // match_phrase - {key: value}
          if (elasticSearchClause[i]['match_phrase']) {
            elasticSearchClause[i]['match_phrase'].forEach(function(bean, key) {
              const value = bean.query ? bean.query : bean;
              this.addWhereClauseEntry(whereClause, key, value, additionalWhereClauseArray);
              hasAdded = true;
            });
          }

          // term - {key: value}
          if (elasticSearchClause[i]['term']) {
            elasticSearchClause[i]['term'].forEach(function(bean, key) {
              const value = bean;
              this.addWhereClauseEntry(whereClause, key, value, additionalWhereClauseArray);
              hasAdded = true;
            });
          }

          if (shouldHandleTerms) {
            // terms - {key: [value1, value2]}
            if (elasticSearchClause[i]['terms']) {
              elasticSearchClause[i]['terms'].forEach(function(bean, key) {
                const value = bean;
                this.addWhereClauseEntry(whereClause, key, value, additionalWhereClauseArray);
              });
            }
          }
        }

      } else {

        const keys = Object.keys(elasticSearchClause);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];

          // term - {key: value}
          if (key === 'term') {
            elasticSearchClause[key].forEach(function(bean, index) {
              const value = bean;
              this.addWhereClauseEntry(whereClause, index, value, additionalWhereClauseArray);
              hasAdded = true;
            });
          }

          if (shouldHandleTerms) {
            // terms - {key: [value1, value2]}
            if (key === 'terms') {
              elasticSearchClause[key].forEach(function(bean, index) {
                const value = bean;
                this.addWhereClauseEntry(whereClause, index, value, additionalWhereClauseArray);
                hasAdded = true;
              });
            }
          }

          // match - {key: {query: value}}
          // match - {key: value}
          if (key === 'match') {
            elasticSearchClause[key].forEach(function(bean, index) {
              const value = bean.query ? bean.query : bean;
              this.addWhereClauseEntry(whereClause, index, value, additionalWhereClauseArray);
              hasAdded = true;
            });
          }

          // match_phrase - {key: {query: value}}
          // match_phrase - {key: value}
          if (key === 'match_phrase') {
            elasticSearchClause[key].forEach(function(bean, index) {
              const value = bean.query ? bean.query : bean;
              this.addWhereClauseEntry(whereClause, index, value, additionalWhereClauseArray);
              hasAdded = true;
            });
          }
        }
      }
    }
    return hasAdded;
  }

  /**
   * add a should clause to a data array. This will be called when the where clause is an array rather than an object
   * @param shouldDataArray - should data array
   * @param key - key
   * @param value - value
   * @param minimumShouldMatch - minimum should match
   */
  private static addShouldClauseEntryToDataArray(shouldDataArray, key, value, minimumShouldMatch) {
    const shouldClauseArray = [];

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const shouldClauseEntry = {
          key: key,
          value: value[i]
        };
        shouldClauseArray.push(shouldClauseEntry);
      }
    } else {
      const whereClauseEntry = {
        key: key,
        value: value
      };

      shouldClauseArray.push(whereClauseEntry);
    }

    const entry = {
      shouldClauseArray: shouldClauseArray,
      minimumShouldMatch: minimumShouldMatch
    };
    shouldDataArray.push(entry);
  }

  /**
   * add each entry in the ElasticSearch terms query to a should query array that can be passed to the
   * dexie where function as an array
   * @param shouldClauseDataArray - shouldClauseDataArray
   * @param elasticSearchClause - elasticSearchClause
   * @param minimumShouldMatch - elasticSearchClause
   * @returns boolean - the result
   */
  public static addTermsQueryToShouldClauseArray(shouldClauseDataArray, elasticSearchClause, minimumShouldMatch) {

    if (!Array.isArray(shouldClauseDataArray)) {
      logger.error('Please pass the should clause map as an array to addTermsQueryToShouldClauseArray');
      return false;
    }

    let hasAdded = false;

    if (elasticSearchClause) {
      if (Array.isArray(elasticSearchClause)) {
        for (let i = 0; i < elasticSearchClause.length; i++) {

          // terms - {key: [value1, value2]}
          if (elasticSearchClause[i]['terms']) {
            elasticSearchClause[i]['terms'].forEach(function(bean, key) {
              const value = bean;
              this.addShouldClauseEntryToDataArray(shouldClauseDataArray, key, value, minimumShouldMatch);

              hasAdded = true;
            });
          }
        }
      } else {
        const keys = Object.keys(elasticSearchClause);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];

          // terms - {key: [value1, value2]}
          if (key === 'terms') {
            elasticSearchClause[key].forEach(function(bean, index) {
              const value = bean;
              this.addShouldClauseEntryToDataArray(shouldClauseDataArray, index, value, minimumShouldMatch);

              hasAdded = true;
            });
          }
        }
      }
    }
    return hasAdded;
  }
}
